import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { toast } from "sonner";
import {
  Wrench,
  Clock,
  CheckCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  Loader2,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Input } from "@/shared/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { maintenanceService } from "@/features/maintenances/services/maintenance.service";
import { maintenanceAdminService } from "@/features/maintenances/services/maintenance-admin.service";
import type {
  MaintenanceResponseDTO,
  MaintenanceStockDTO,
} from "@/features/maintenances/types/maintenance.types";
import { getApiErrorMessage } from "@/shared/lib/api-error";

const CONDITION_LABELS: Record<string, string> = {
  NEW: "Novo",
  USED: "Usado",
  WORN_OUT: "Desgastado",
};

function translateCondition(condition: string): string {
  return CONDITION_LABELS[condition] ?? condition;
}

function getStockCode(stock: MaintenanceStockDTO): string {
  return stock.stockCode ?? stock.code ?? stock.barcode;
}

function getStockDescription(stock: MaintenanceStockDTO): string | null {
  return stock.color ?? stock.description ?? null;
}

function formatDays(days: number): string {
  return days === 1 ? "1 dia" : `${days} dias`;
}

function calcEstimatedDays(
  startAt: string,
  expectedEndAt: string | null,
): number | null {
  if (!expectedEndAt) return null;
  const diff = new Date(expectedEndAt).getTime() - new Date(startAt).getTime();
  return Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function addDaysToDate(isoDate: string | null, days: number): string {
  const base = isoDate ? new Date(isoDate) : new Date();
  base.setDate(base.getDate() + days);
  return base.toISOString();
}

interface FilterDropdownProps<T> {
  label: string;
  placeholder: string;
  value: string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  open: boolean;
  loading: boolean;
  items: T[];
  emptyText: string;
  monospace?: boolean;
  getKey: (item: T) => string;
  renderLabel: (item: T) => React.ReactNode;
  onChange: (value: string) => void;
  onFocus: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSelect: (item: T) => void;
}

function FilterDropdown<T>({
  label,
  placeholder,
  value,
  containerRef,
  open,
  loading,
  items,
  emptyText,
  monospace,
  getKey,
  renderLabel,
  onChange,
  onFocus,
  onKeyDown,
  onSelect,
}: FilterDropdownProps<T>) {
  return (
    <div ref={containerRef} className="flex-1 relative space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
      />
      {open && value.trim() && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-md border border-input bg-background shadow-md">
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Buscando...
            </div>
          ) : items.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              {emptyText}
            </p>
          ) : (
            <ul className="max-h-48 overflow-y-auto py-1">
              {items.map((item) => (
                <li key={getKey(item)}>
                  <button
                    type="button"
                    onClick={() => onSelect(item)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors${monospace ? " font-mono text-xs" : ""}`}
                  >
                    {renderLabel(item)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function ManutencaoPage() {
  const { isAdmin } = useAuth();
  const service = isAdmin ? maintenanceAdminService : maintenanceService;

  const [maintenances, setMaintenances] = useState<MaintenanceResponseDTO[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalInMaintenance, setTotalInMaintenance] = useState(0);
  const [first, setFirst] = useState(true);
  const [last, setLast] = useState(false);

  const [quickSearch, setQuickSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);

  const [filterCode, setFilterCode] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [filterSize, setFilterSize] = useState("");

  const [codeDropdownItems, setCodeDropdownItems] = useState<
    MaintenanceResponseDTO[]
  >([]);
  const [codeDropdownLoading, setCodeDropdownLoading] = useState(false);

  const [productDropdownItems, setProductDropdownItems] = useState<string[]>(
    [],
  );
  const [productDropdownLoading, setProductDropdownLoading] = useState(false);

  const [sizeDropdownItems, setSizeDropdownItems] = useState<string[]>([]);
  const [sizeDropdownLoading, setSizeDropdownLoading] = useState(false);

  const [codeDropdownOpen, setCodeDropdownOpen] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
  const codeContainerRef = useRef<HTMLDivElement>(null);
  const productContainerRef = useRef<HTMLDivElement>(null);
  const sizeContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!codeContainerRef.current?.contains(target))
        setCodeDropdownOpen(false);
      if (!productContainerRef.current?.contains(target))
        setProductDropdownOpen(false);
      if (!sizeContainerRef.current?.contains(target))
        setSizeDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [selectedMaintenance, setSelectedMaintenance] =
    useState<MaintenanceResponseDTO | null>(null);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [additionalDays, setAdditionalDays] = useState(1);

  const openMaintenances = useMemo(
    () => maintenances.filter((m) => m.endAt === null),
    [maintenances],
  );

  const prazoMedio = useMemo(() => {
    const days = openMaintenances
      .map((m) => calcEstimatedDays(m.startAt, m.expectedEndAt))
      .filter((d): d is number => d !== null);
    if (days.length === 0) return "-";
    const min = Math.min(...days);
    const max = Math.max(...days);
    return min === max ? formatDays(min) : `${min}-${max} dias`;
  }, [openMaintenances]);

  const filteredMaintenances = useMemo(() => {
    if (!quickSearch.trim()) return openMaintenances;
    const q = quickSearch.toLowerCase();
    return openMaintenances.filter(
      (m) =>
        getStockCode(m.stock).toLowerCase().includes(q) ||
        m.stock.productName.toLowerCase().includes(q) ||
        m.stock.size.toLowerCase().includes(q) ||
        translateCondition(m.stock.condition).toLowerCase().includes(q),
    );
  }, [openMaintenances, quickSearch]);

  const fetchCode = useCallback(
    (term: string, signal: AbortSignal) =>
      service
        .listPaginated(
          {
            barcode: term,
            active: true,
            size: 8,
            sortBy: "createdAt",
            sortDirection: "DESC",
          },
          { signal },
        )
        .then((res) => res.content),
    [service],
  );

  const fetchProduct = useCallback(
    async (term: string, signal: AbortSignal) => {
      const res = await service.listPaginated(
        {
          productName: term,
          active: true,
          size: 8,
          sortBy: "createdAt",
          sortDirection: "DESC",
        },
        { signal },
      );
      const seen = new Set<string>();
      const names: string[] = [];
      for (const m of res.content) {
        if (!seen.has(m.stock.productName)) {
          seen.add(m.stock.productName);
          names.push(m.stock.productName);
        }
      }
      return names;
    },
    [service],
  );

  const fetchSize = useCallback(
    async (term: string, signal: AbortSignal) => {
      const res = await service.listPaginated(
        {
          stockSize: term,
          active: true,
          size: 8,
          sortBy: "createdAt",
          sortDirection: "DESC",
        },
        { signal },
      );
      const seen = new Set<string>();
      const sizes: string[] = [];
      for (const m of res.content) {
        if (!seen.has(m.stock.size)) {
          seen.add(m.stock.size);
          sizes.push(m.stock.size);
        }
      }
      return sizes;
    },
    [service],
  );

  const searchCode = useDebouncedSearch({
    fetcher: fetchCode,
    setItems: setCodeDropdownItems,
    setLoading: setCodeDropdownLoading,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar códigos")),
    debounceMs: 350,
  });
  const searchProduct = useDebouncedSearch({
    fetcher: fetchProduct,
    setItems: setProductDropdownItems,
    setLoading: setProductDropdownLoading,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar produtos")),
    debounceMs: 350,
  });
  const searchSize = useDebouncedSearch({
    fetcher: fetchSize,
    setItems: setSizeDropdownItems,
    setLoading: setSizeDropdownLoading,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar tamanhos")),
    debounceMs: 350,
  });

  const loadMaintenances = useCallback(
    async (
      page = 0,
      code = filterCode,
      product = filterProduct,
      size = filterSize,
    ) => {
      setLoading(true);
      try {
        const response = await service.listPaginated({
          page,
          size: pageSize,
          active: true,
          sortBy: "createdAt",
          sortDirection: "DESC",
          ...(code.trim() ? { barcode: code.trim() } : {}),
          ...(product.trim() ? { productName: product.trim() } : {}),
          ...(size.trim() ? { stockSize: size.trim() } : {}),
        });
        setMaintenances(response.content);
        setTotalPages(response.totalPages);
        setTotalInMaintenance(response.totalInMaintenance);
        setCurrentPage(page);
        setFirst(response.first);
        setLast(response.last);
      } catch (error: unknown) {
        toast.error(getApiErrorMessage(error, "Erro ao carregar manutenções"));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAdmin, pageSize, filterCode, filterProduct, filterSize],
  );

  const closeAllDropdowns = () => {
    setCodeDropdownOpen(false);
    setProductDropdownOpen(false);
    setSizeDropdownOpen(false);
  };

  const handleSearch = () => {
    closeAllDropdowns();
    loadMaintenances(0, filterCode, filterProduct, filterSize);
  };

  const handleClearFilters = () => {
    setFilterCode("");
    setFilterProduct("");
    setFilterSize("");
    setCodeDropdownItems([]);
    setProductDropdownItems([]);
    setSizeDropdownItems([]);
    closeAllDropdowns();
    setQuickSearch("");
    loadMaintenances(0, "", "", "");
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadMaintenances(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleOpenFinish = (maintenance: MaintenanceResponseDTO) => {
    setSelectedMaintenance(maintenance);
    setIsFinishModalOpen(true);
  };

  const handleOpenExtend = (maintenance: MaintenanceResponseDTO) => {
    setSelectedMaintenance(maintenance);
    setAdditionalDays(1);
    setIsExtendModalOpen(true);
  };

  const handleCloseFinish = () => {
    setIsFinishModalOpen(false);
    setTimeout(() => setSelectedMaintenance(null), 300);
  };

  const handleCloseExtend = () => {
    setIsExtendModalOpen(false);
    setTimeout(() => {
      setSelectedMaintenance(null);
      setAdditionalDays(1);
    }, 300);
  };

  const handleFinish = useCallback(async () => {
    if (!selectedMaintenance) return;
    setActionLoading(true);
    const id = selectedMaintenance.idStockMaintenance;
    try {
      await service.finish(id);
      setMaintenances((prev) =>
        prev.map((m) =>
          m.idStockMaintenance === id
            ? { ...m, endAt: new Date().toISOString() }
            : m,
        ),
      );
      setTotalInMaintenance((prev) => Math.max(0, prev - 1));
      toast.success("Manutenção liberada com sucesso!");
      handleCloseFinish();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao liberar manutenção"));
    } finally {
      setActionLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaintenance, isAdmin]);

  const handleExtend = useCallback(async () => {
    if (!selectedMaintenance) return;
    setActionLoading(true);
    try {
      const newExpectedEndAt = addDaysToDate(
        selectedMaintenance.expectedEndAt,
        additionalDays,
      );
      const updated = await service.extend(
        selectedMaintenance.idStockMaintenance,
        { newExpectedEndAt },
      );
      setMaintenances((prev) =>
        prev.map((m) =>
          m.idStockMaintenance === updated.idStockMaintenance ? updated : m,
        ),
      );
      toast.success("Manutenção estendida com sucesso!");
      handleCloseExtend();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao estender manutenção"));
    } finally {
      setActionLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaintenance, additionalDays, isAdmin]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) loadMaintenances(newPage);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">
          Manutenção de Estoque
        </h1>
        <p className="text-muted-foreground">
          Gerencie peças em manutenção e controle prazos
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
          <CardDescription>Busque e filtre peças em manutenção</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <FilterDropdown
              label="Código"
              placeholder="Digite o código..."
              value={filterCode}
              containerRef={codeContainerRef}
              open={codeDropdownOpen}
              loading={codeDropdownLoading}
              items={codeDropdownItems}
              emptyText="Nenhum código encontrado"
              monospace
              getKey={(m) => String(m.idStockMaintenance)}
              renderLabel={(m) => getStockCode(m.stock)}
              onChange={(v) => {
                setFilterCode(v);
                setCodeDropdownOpen(true);
                searchCode(v);
              }}
              onFocus={() => filterCode.trim() && setCodeDropdownOpen(true)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              onSelect={(m) => {
                setFilterCode(getStockCode(m.stock));
                setCodeDropdownOpen(false);
              }}
            />
            <FilterDropdown
              label="Produto"
              placeholder="Digite o produto..."
              value={filterProduct}
              containerRef={productContainerRef}
              open={productDropdownOpen}
              loading={productDropdownLoading}
              items={productDropdownItems}
              emptyText="Nenhum produto encontrado"
              getKey={(name) => name}
              renderLabel={(name) => name}
              onChange={(v) => {
                setFilterProduct(v);
                setProductDropdownOpen(true);
                searchProduct(v);
              }}
              onFocus={() =>
                filterProduct.trim() && setProductDropdownOpen(true)
              }
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              onSelect={(name) => {
                setFilterProduct(name);
                setProductDropdownOpen(false);
              }}
            />
            <FilterDropdown
              label="Tamanho"
              placeholder="Digite o tamanho..."
              value={filterSize}
              containerRef={sizeContainerRef}
              open={sizeDropdownOpen}
              loading={sizeDropdownLoading}
              items={sizeDropdownItems}
              emptyText="Nenhum tamanho encontrado"
              getKey={(size) => size}
              renderLabel={(size) => size}
              onChange={(v) => {
                setFilterSize(v);
                setSizeDropdownOpen(true);
                searchSize(v);
              }}
              onFocus={() => filterSize.trim() && setSizeDropdownOpen(true)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              onSelect={(size) => {
                setFilterSize(size);
                setSizeDropdownOpen(false);
              }}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSearch} className="shrink-0">
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
            <Button onClick={handleClearFilters} variant="outline">
              Limpar filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent>
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Wrench className="w-4 h-4" />
              Em Manutenção
            </div>
            <div className="mt-2 text-3xl font-bold text-orange-400">
              {loading ? "—" : totalInMaintenance}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Clock className="w-4 h-4" />
              Prazo Médio
            </div>
            <div className="mt-2 text-3xl font-bold text-foreground">
              {loading ? "—" : prazoMedio}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="text-base">Peças em Manutenção</CardTitle>
            <CardDescription className="mt-1">
              {loading
                ? "Carregando..."
                : quickSearch
                  ? `${filteredMaintenances.length} de ${openMaintenances.length} peça(s)`
                  : `${openMaintenances.length} peça(s) em manutenção`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Buscar por código, produto, tamanho, condição..."
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              className="w-72"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => loadMaintenances(currentPage)}
              title="Atualizar"
              disabled={loading}
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </Button>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                loadMaintenances(0);
              }}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value={5}>5 / página</option>
              <option value={10}>10 / página</option>
              <option value={25}>25 / página</option>
              <option value={50}>50 / página</option>
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {loading && maintenances.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Carregando manutenções...
            </div>
          ) : filteredMaintenances.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {quickSearch
                ? "Nenhum resultado para a busca."
                : "Nenhuma peça em manutenção no momento."}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="pb-3 pr-4 font-medium">Código</th>
                      <th className="pb-3 pr-4 font-medium">Produto</th>
                      <th className="pb-3 pr-4 font-medium">Tamanho</th>
                      <th className="pb-3 pr-4 font-medium">Condição</th>
                      <th className="pb-3 pr-4 font-medium">Prazo Estimado</th>
                      <th className="pb-3 font-medium text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMaintenances.map((m) => {
                      const days = calcEstimatedDays(
                        m.startAt,
                        m.expectedEndAt,
                      );
                      const description = getStockDescription(m.stock);
                      return (
                        <tr
                          key={m.idStockMaintenance}
                          className="border-b border-border last:border-0"
                        >
                          <td className="py-4 pr-4 font-mono text-xs">
                            {getStockCode(m.stock)}
                          </td>
                          <td className="py-4 pr-4">
                            <p className="font-medium text-foreground">
                              {m.stock.productName}
                            </p>
                            {description && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {description}
                              </p>
                            )}
                          </td>
                          <td className="py-4 pr-4">{m.stock.size}</td>
                          <td className="py-4 pr-4">
                            <Badge variant="secondary">
                              {translateCondition(m.stock.condition)}
                            </Badge>
                          </td>
                          <td className="py-4 pr-4">
                            <span className="flex items-center gap-1.5 text-muted-foreground">
                              <Calendar className="w-3.5 h-3.5" />
                              {days != null ? formatDays(days) : "-"}
                            </span>
                          </td>
                          <td className="py-4">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-1.5"
                                disabled={actionLoading}
                                onClick={() => handleOpenFinish(m)}
                              >
                                <CheckCircle className="w-3.5 h-3.5" />
                                Liberar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-1.5"
                                disabled={actionLoading}
                                onClick={() => handleOpenExtend(m)}
                              >
                                <Clock className="w-3.5 h-3.5" />
                                Estender
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-6 text-sm text-muted-foreground">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={first || loading}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Anterior
                  </Button>
                  <span>
                    Página {currentPage + 1} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={last || loading}
                  >
                    Próxima
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={isFinishModalOpen}
        onOpenChange={(open) => !open && handleCloseFinish()}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Liberar Peça da Manutenção</DialogTitle>
            <DialogDescription>
              Confirme que a manutenção foi concluída
            </DialogDescription>
          </DialogHeader>
          {selectedMaintenance && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="font-semibold text-foreground">
                  {selectedMaintenance.stock.productName}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Código: {getStockCode(selectedMaintenance.stock)} | Tamanho:{" "}
                  {selectedMaintenance.stock.size}
                </p>
              </div>
              <div className="rounded-lg border border-green-500/30 bg-green-500/20 p-4 flex gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-400">
                    Peça pronta para uso
                  </p>
                  <p className="text-xs text-green-400/70 mt-0.5">
                    A peça será marcada como "disponível" e poderá ser alugada
                    novamente
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleCloseFinish}
              disabled={actionLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleFinish}
              disabled={actionLoading}
              className="gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              {actionLoading ? "Liberando..." : "Confirmar Liberação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isExtendModalOpen}
        onOpenChange={(open) => !open && handleCloseExtend()}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Estender Prazo de Manutenção</DialogTitle>
            <DialogDescription>
              Adicione mais tempo para a manutenção desta peça
            </DialogDescription>
          </DialogHeader>
          {selectedMaintenance && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="font-semibold text-foreground">
                  {selectedMaintenance.stock.productName}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Código: {getStockCode(selectedMaintenance.stock)} | Tamanho:{" "}
                  {selectedMaintenance.stock.size}
                </p>
              </div>
              <div className="space-y-2">
                <label
                  className="text-sm font-medium text-foreground"
                  htmlFor="additionalDays"
                >
                  Dias adicionais
                </label>
                <input
                  id="additionalDays"
                  type="number"
                  min={1}
                  value={additionalDays}
                  onChange={(e) =>
                    setAdditionalDays(Math.max(1, Number(e.target.value)))
                  }
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={actionLoading}
                />
                <p className="text-xs text-muted-foreground">
                  A manutenção será estendida por {additionalDays} dia(s)
                  adicional(is)
                </p>
              </div>
              <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/20 p-4 flex gap-3">
                <Clock className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-yellow-400">
                    Prazo estendido
                  </p>
                  <p className="text-xs text-yellow-400/70 mt-0.5">
                    A peça continuará indisponível pelo período adicional
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleCloseExtend}
              disabled={actionLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleExtend}
              disabled={actionLoading}
              className="gap-2"
            >
              <Clock className="w-4 h-4" />
              {actionLoading ? "Estendendo..." : "Estender Manutenção"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
